import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import employmentHistoryService from '../services/employmentHistoryService';
import styles from '../assets/employmentHistoryEdit.module.css';
import Loader from '../components/Loader';

const EmploymentHistoryEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employmentHistory, setEmploymentHistory] = useState(null);
  const [formData, setFormData] = useState({
    date_start: '',
    date_end: '',
    still_in_employment: 0,
    company_name: '',
    address_1: '',
    address_2: '',
    address_3: '',
    town: '',
    postcode: '',
    referee_name: '',
    referee_telephone: '',
    referee_email: '',
    job_title: '',
    reason_for_living: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadEmploymentHistory();
  }, [id]);

  const loadEmploymentHistory = async () => {
    try {
      setLoading(true);

      // Fetch employment history from API
      const data = await employmentHistoryService.getById(id);
      console.log('📥 [EMPLOYMENT_HISTORY_EDIT] Fetched data:', data);

      setEmploymentHistory(data);

      // Convert dates to DD/MM/YYYY format for display
      setFormData({
        date_start: data.date_start ? formatDateForInput(data.date_start) : '',
        date_end: data.date_end ? formatDateForInput(data.date_end) : '',
        still_in_employment: data.still_in_employment || 0,
        company_name: data.company_name || '',
        address_1: data.address_1 || '',
        address_2: data.address_2 || '',
        address_3: data.address_3 || '',
        town: data.town || '',
        postcode: data.postcode || '',
        referee_name: data.referee_name || '',
        referee_telephone: data.referee_telephone || '',
        referee_email: data.referee_email || '',
        job_title: data.job_title || '',
        reason_for_living: data.reason_for_living || ''
      });

    } catch (err) {
      console.error('Error loading employment history:', err);
      toast.error('Failed to load employment history');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1; // JavaScript months are 0-indexed
    const year = parseInt(parts[2]);
    return new Date(year, month, day);
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    if (!formData.address_1.trim()) {
      newErrors.address_1 = 'Address 1 is required';
    }
    if (!formData.town.trim()) {
      newErrors.town = 'Town is required';
    }
    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required';
    }
    if (!formData.job_title.trim()) {
      newErrors.job_title = 'Job title is required';
    }

    // Date validation
    if (!formData.date_start.trim()) {
      newErrors.date_start = 'Start date is required';
    } else {
      const startDate = parseDate(formData.date_start);
      if (!startDate || isNaN(startDate.getTime())) {
        newErrors.date_start = 'Start date must be in DD/MM/YYYY format';
      }
    }

    // End date validation (only if not still employed)
    if (parseInt(formData.still_in_employment) === 0) {
      if (!formData.date_end.trim()) {
        newErrors.date_end = 'End date is required when not still employed';
      } else {
        const endDate = parseDate(formData.date_end);
        if (!endDate || isNaN(endDate.getTime())) {
          newErrors.date_end = 'End date must be in DD/MM/YYYY format';
        } else if (formData.date_start.trim()) {
          const startDate = parseDate(formData.date_start);
          if (startDate && endDate <= startDate) {
            newErrors.date_end = 'End date must be after start date';
          }
        }
      }
    }

    // Postcode pattern validation (UK format)
    if (formData.postcode.trim()) {
      const postcodePattern = /^[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]?\s?[0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2}$/;
      if (!postcodePattern.test(formData.postcode)) {
        newErrors.postcode = 'Please enter a valid UK postcode';
      }
    }

    // Field length validation
    const maxLengths = {
      company_name: 255,
      address_1: 255,
      address_2: 255,
      address_3: 255,
      town: 255,
      postcode: 255,
      referee_name: 255,
      referee_telephone: 255,
      referee_email: 255,
      job_title: 255,
      reason_for_living: 255
    };

    Object.keys(maxLengths).forEach(field => {
      if (formData[field] && formData[field].length > maxLengths[field]) {
        newErrors[field] = `${field.replace('_', ' ')} must not exceed ${maxLengths[field]} characters`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleStillEmployedChange = (e) => {
    const value = parseInt(e.target.value);
    setFormData(prev => ({
      ...prev,
      still_in_employment: value,
      // Clear end date if still employed
      date_end: value === 1 ? '' : prev.date_end
    }));

    // Clear end date error if still employed
    if (value === 1 && errors.date_end) {
      setErrors(prev => ({
        ...prev,
        date_end: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    try {
      setSaving(true);

      // Update employment history via API
      console.log('💾 [EMPLOYMENT_HISTORY_EDIT] Updating employment history:', formData);
      await employmentHistoryService.update(id, formData);

      toast.success('Employment history updated successfully');
      navigate(-1); // Go back to previous page

    } catch (err) {
      console.error('Error updating employment history:', err);
      toast.error('Failed to update employment history');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!employmentHistory) {
    return (
      <div className={styles.container}>
        <div className="row">
          <div className="col-md-12">
            <div className="alert alert-danger">
              Employment history not found
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className="row">
        <div className="col-md-12">
          <ol className={styles.breadcrumb}>
            <li><a href="#" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>Dashboard</a></li>
            <li><a href="#" onClick={(e) => { e.preventDefault(); navigate(-1); }}>Back</a></li>
            <li className={styles.active}>Edit employment history</li>
          </ol>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelForm}`}>
            <div className={`${styles.panelHeading} ${styles.panelTitle}`}>
              <h3>Edit employment history</h3>
            </div>

            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} className={styles.formHorizontal}>
                <fieldset>
                  {/* Date Start */}
                  <div className={`${styles.formGroup} ${errors.date_start ? styles.hasError : ''}`}>
                    <label htmlFor="date_start" className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Date start
                    </label>
                    <div className={styles.colSm2}>
                      <input
                        type="text"
                        id="date_start"
                        name="date_start"
                        value={formData.date_start}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${styles.jsDatepicker}`}
                        placeholder="DD/MM/YYYY"
                        required
                      />
                      {errors.date_start && <span className={styles.helpBlock}>{errors.date_start}</span>}
                    </div>
                  </div>

                  {/* Date End and Still in Employment */}
                  <div className={`${styles.formGroup} ${errors.date_end ? styles.hasError : ''}`}>
                    <label htmlFor="date_end" className={`${styles.controlLabel} ${styles.colSm3} ${parseInt(formData.still_in_employment) === 0 ? styles.labelRequired : ''}`}>
                      Date end
                    </label>
                    <div className={styles.colSm2}>
                      <input
                        type="text"
                        id="date_end"
                        name="date_end"
                        value={formData.date_end}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${styles.jsDatepicker}`}
                        placeholder="DD/MM/YYYY"
                        disabled={parseInt(formData.still_in_employment) === 1}
                        required={parseInt(formData.still_in_employment) === 0}
                      />
                      {errors.date_end && <span className={styles.helpBlock}>{errors.date_end}</span>}
                    </div>

                    <div id="js-employment-history-checkbox"></div>
                    <label htmlFor="still_in_employment" className={`${styles.controlLabel} ${styles.colSm2}`}>
                      Still in employment
                    </label>
                    <div className={styles.colSm1}>
                      <select
                        id="still_in_employment"
                        name="still_in_employment"
                        value={formData.still_in_employment}
                        onChange={handleStillEmployedChange}
                        className={`${styles.formControl} ${styles.smallerSelectFs}`}
                      >
                        <option value="0">No</option>
                        <option value="1">Yes</option>
                      </select>
                    </div>
                  </div>

                  {/* Company Name */}
                  <div className={`${styles.formGroup} ${errors.company_name ? styles.hasError : ''}`}>
                    <label htmlFor="company_name" className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Company name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      />
                      {errors.company_name && <span className={styles.helpBlock}>{errors.company_name}</span>}
                    </div>
                  </div>

                  {/* Address 1 */}
                  <div className={`${styles.formGroup} ${errors.address_1 ? styles.hasError : ''}`}>
                    <label htmlFor="address_1" className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Address 1
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="address_1"
                        name="address_1"
                        value={formData.address_1}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      />
                      {errors.address_1 && <span className={styles.helpBlock}>{errors.address_1}</span>}
                    </div>
                  </div>

                  {/* Address 2 */}
                  <div className={`${styles.formGroup} ${errors.address_2 ? styles.hasError : ''}`}>
                    <label htmlFor="address_2" className={`${styles.controlLabel} ${styles.colSm3}`}>
                      Address 2
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="address_2"
                        name="address_2"
                        value={formData.address_2}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                      {errors.address_2 && <span className={styles.helpBlock}>{errors.address_2}</span>}
                    </div>
                  </div>

                  {/* Address 3 */}
                  <div className={`${styles.formGroup} ${errors.address_3 ? styles.hasError : ''}`}>
                    <label htmlFor="address_3" className={`${styles.controlLabel} ${styles.colSm3}`}>
                      Address 3
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="address_3"
                        name="address_3"
                        value={formData.address_3}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                      {errors.address_3 && <span className={styles.helpBlock}>{errors.address_3}</span>}
                    </div>
                  </div>

                  {/* Town */}
                  <div className={`${styles.formGroup} ${errors.town ? styles.hasError : ''}`}>
                    <label htmlFor="town" className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Town
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="town"
                        name="town"
                        value={formData.town}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      />
                      {errors.town && <span className={styles.helpBlock}>{errors.town}</span>}
                    </div>
                  </div>

                  {/* Postcode */}
                  <div className={`${styles.formGroup} ${errors.postcode ? styles.hasError : ''}`}>
                    <label htmlFor="postcode" className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Postcode
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="postcode"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        pattern="[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]? [0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2}"
                        required
                      />
                      {errors.postcode && <span className={styles.helpBlock}>{errors.postcode}</span>}
                    </div>
                  </div>

                  {/* Referee Name */}
                  <div className={`${styles.formGroup} ${errors.referee_name ? styles.hasError : ''}`}>
                    <label htmlFor="referee_name" className={`${styles.controlLabel} ${styles.colSm3}`}>
                      Referee name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="referee_name"
                        name="referee_name"
                        value={formData.referee_name}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                      {errors.referee_name && <span className={styles.helpBlock}>{errors.referee_name}</span>}
                    </div>
                  </div>

                  {/* Referee Telephone */}
                  <div className={`${styles.formGroup} ${errors.referee_telephone ? styles.hasError : ''}`}>
                    <label htmlFor="referee_telephone" className={`${styles.controlLabel} ${styles.colSm3}`}>
                      Referee telephone
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="referee_telephone"
                        name="referee_telephone"
                        value={formData.referee_telephone}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                      {errors.referee_telephone && <span className={styles.helpBlock}>{errors.referee_telephone}</span>}
                    </div>
                  </div>

                  {/* Referee Email */}
                  <div className={`${styles.formGroup} ${errors.referee_email ? styles.hasError : ''}`}>
                    <label htmlFor="referee_email" className={`${styles.controlLabel} ${styles.colSm3}`}>
                      Referee email
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="email"
                        id="referee_email"
                        name="referee_email"
                        value={formData.referee_email}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                      {errors.referee_email && <span className={styles.helpBlock}>{errors.referee_email}</span>}
                    </div>
                  </div>

                  {/* Job Title */}
                  <div className={`${styles.formGroup} ${errors.job_title ? styles.hasError : ''}`}>
                    <label htmlFor="job_title" className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Job title
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="job_title"
                        name="job_title"
                        value={formData.job_title}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      />
                      {errors.job_title && <span className={styles.helpBlock}>{errors.job_title}</span>}
                    </div>
                  </div>

                  {/* Reason for Leaving */}
                  <div className={`${styles.formGroup} ${errors.reason_for_living ? styles.hasError : ''}`}>
                    <label htmlFor="reason_for_living" className={`${styles.controlLabel} ${styles.colSm3}`}>
                      Reason for leaving
                    </label>
                    <div className={styles.colSm5}>
                      <textarea
                        id="reason_for_living"
                        name="reason_for_living"
                        value={formData.reason_for_living}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        rows="3"
                      />
                      {errors.reason_for_living && <span className={styles.helpBlock}>{errors.reason_for_living}</span>}
                    </div>
                  </div>
                </fieldset>

                {/* Submit Button */}
                <div className={`${styles.formGroup} ${styles.textRight}`}>
                  <button
                    type="submit"
                    className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                    disabled={saving}
                  >
                    <i className="glyphicon glyphicon-edit"></i>
                    {saving ? ' Updating...' : ' Edit employment history'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmploymentHistoryEdit;